import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { toast } from "sonner";
import { Home, Link2, ShieldCheck, Mail, Phone, ArrowRight, Loader2, Unlink } from "lucide-react";
import { V } from "@/lib/vocab";

// Self-service: link your staff account to your own resident (roster) profile so both roles
// share one identity. Ownership is proven with a one-time code sent to the resident contact.
export function ResidentLinkCard() {
  const [state, setState] = useState(null); // {linked, person}
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("sms");
  const [contact, setContact] = useState("");
  const [stage, setStage] = useState("idle"); // idle | code
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/me/resident-link"); setState(r.data); }
    catch { setState({ linked: false }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendCode = async () => {
    if (!contact.trim()) { toast.error(method === "sms" ? `Enter your ${V.lc("person")} mobile` : `Enter your ${V.lc("person")} email`); return; }
    setBusy(true);
    try {
      const r = await api.post("/me/resident-link/start", { method, contact: contact.trim() });
      setMasked(r.data.masked || "");
      setStage("code");
      toast.success(r.data.sent ? `Code sent to ${r.data.masked}` : `If that matches ${V.a("person")}, a code is on its way.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a code"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/me/resident-link/verify", { code: code.trim() });
      setState(r.data); setStage("idle"); setCode(""); setContact("");
      toast.success(`${V.one("person")} profile linked!`);
    } catch (e) { toast.error(e?.response?.data?.detail || "That code didn't work"); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    setBusy(true);
    try { await api.delete("/me/resident-link"); setState({ linked: false }); toast.success(`${V.one("person")} profile unlinked`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't unlink"); }
    finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try { await api.post("/me/resident-link/open", {}); window.location.href = "/portal"; }
    catch (e) { toast.error(e?.response?.data?.detail || `Couldn't open your ${V.lc("person")} home`); setBusy(false); }
  };

  if (loading) return null;

  return (
    <div data-testid="resident-link-card" className="rounded-2xl border border-sand-200 bg-white p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <Home className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
        <h2 className="font-heading text-lg font-bold text-ink">Your {V.lc("person")} profile</h2>
      </div>

      {state?.linked ? (
        <>
          <p className="text-sm text-muted-foreground mb-4">Your team account and your {V.lc("person")} profile are one — you can jump into your {V.lc("person")} home anytime.</p>
          <div data-testid="resident-link-linked" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-ink truncate">{state.person?.name || `Linked ${V.lc("person")}`}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[homeLabel(state.person), state.person?.community].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid="open-resident-home" onClick={openPortal} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-bold px-5 py-2.5 hover:bg-ink/90 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Home className="h-4 w-4" />} Open my {V.lc("person")} home <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="resident-unlink" onClick={unlink} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/60 hover:text-ink font-semibold px-4 py-2.5 transition-colors">
              <Unlink className="h-4 w-4" /> Unlink
            </button>
          </div>
        </>
      ) : stage === "idle" ? (
        <>
          <p className="text-sm text-muted-foreground mb-4">Use a different email or number as {V.a("person")} than for your team login? Link them so both roles share one profile. We'll text or email a one-time code to confirm it's you.</p>
          <div className="inline-flex rounded-full bg-sand-100 p-1 mb-3">
            {[["sms", "Text a code", Phone], ["email", "Email a code", Mail]].map(([v, label, Icon]) => (
              <button key={v} data-testid={`link-method-${v}`} onClick={() => setMethod(v)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${method === v ? "bg-white text-ink shadow-sm" : "text-ink/50"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="resident-link-contact" value={contact} onChange={(e) => setContact(e.target.value)}
              placeholder={method === "sms" ? `Your ${V.lc("person")} mobile` : `Your ${V.lc("person")} email`}
              className="flex-1 rounded-xl border border-sand-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <button data-testid="resident-link-send" onClick={sendCode} disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-redline-500 text-white font-bold px-5 py-2.5 hover:bg-[#E5484D] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Send code
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">Enter the 6-digit code we sent to <span className="font-semibold text-ink">{masked}</span>.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="resident-link-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder="123456"
              className="flex-1 rounded-xl border border-sand-200 px-3 py-2.5 text-sm tracking-widest font-mono outline-none focus:ring-2 focus:ring-redline-500/30" />
            <button data-testid="resident-link-verify" onClick={verify} disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-bold px-5 py-2.5 hover:bg-ink/90 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify &amp; link
            </button>
          </div>
          <button data-testid="resident-link-restart" onClick={() => { setStage("idle"); setCode(""); }}
            className="text-xs text-ink/70 hover:text-ink font-semibold underline underline-offset-2 mt-3">Use a different email or number</button>
        </>
      )}
    </div>
  );
}
